import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getAllEnabledProducts } from "../../../api";
import { buildRegisterPath } from "../../../utils/affiliateTracking";
import {
  PLAN_FEATURES,
  getMonthlyEquivalent,
  getPlanPeriod,
  sortPlansByCycle,
} from "../constants";
import { Container, SectionTitle, SectionLead } from "../styles";
import {
  PricingSection,
  PricingGlow,
  PricingGrid,
  PlanCard,
  PopularBadge,
  SavingsBadge,
  PlanHead,
  PlanName,
  PlanDescription,
  PriceBlock,
  PriceRow,
  PriceAmount,
  PricePeriod,
  PriceMeta,
  FeatureList,
  FeatureItem,
  FeatureCheck,
  PlanCta,
  Guarantee,
  PricingStatus,
} from "./HomepagePricing.styles";

const HomepagePricing = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    getAllEnabledProducts()
      .then((response) => {
        if (mounted && Array.isArray(response?.data)) {
          setProducts(response.data);
        }
      })
      .catch(() => {
        if (mounted) {
          setProducts([]);
        }
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  const plans = useMemo(() => sortPlansByCycle(products), [products]);

  return (
    <PricingSection id="pricing">
      <PricingGlow aria-hidden="true" />
      <Container>
        <SectionTitle>
          Choose Your <span>Access Level</span>
        </SectionTitle>
        <SectionLead>
          Join thousands of investors gaining an edge with institutional-grade
          analysis
        </SectionLead>

        {loading ? (
          <PricingStatus>Loading plans…</PricingStatus>
        ) : plans.length === 0 ? (
          <PricingStatus>
            Plans are temporarily unavailable. Please try again shortly.
          </PricingStatus>
        ) : (
          <PricingGrid>
            {plans.map((plan) => {
              const isPopular = plan.name.toLowerCase() === "quarterly";
              const monthlyEquivalent = getMonthlyEquivalent(
                plan.cost,
                plan.billingCycle
              );
              const savingsLabel = plan.marketingPhrase?.trim();

              return (
                <PlanCard key={plan.id} $popular={isPopular}>
                  {isPopular && <PopularBadge>Most Popular</PopularBadge>}
                  {savingsLabel && (
                    <SavingsBadge>{savingsLabel}</SavingsBadge>
                  )}

                  <PlanHead $hasSavings={Boolean(savingsLabel)}>
                    <PlanName>{plan.name}</PlanName>
                    <PlanDescription>{plan.description}</PlanDescription>
                  </PlanHead>

                  <PriceBlock>
                    <PriceRow>
                      <PriceAmount>${plan.cost}</PriceAmount>
                      <PricePeriod>
                        {getPlanPeriod(plan.billingCycle)}
                      </PricePeriod>
                    </PriceRow>
                    {monthlyEquivalent != null && (
                      <PriceMeta>~${monthlyEquivalent}/mo</PriceMeta>
                    )}
                  </PriceBlock>

                  <FeatureList>
                    {PLAN_FEATURES.map((feature) => (
                      <FeatureItem key={feature}>
                        <FeatureCheck aria-hidden="true">
                          <span className="material-icons">check</span>
                        </FeatureCheck>
                        {feature}
                      </FeatureItem>
                    ))}
                  </FeatureList>

                  <PlanCta
                    as={Link}
                    to={buildRegisterPath(plan.id)}
                    $popular={isPopular}
                  >
                    Get Started
                  </PlanCta>
                </PlanCard>
              );
            })}
          </PricingGrid>
        )}

        <Guarantee>Cancel Anytime</Guarantee>
      </Container>
    </PricingSection>
  );
};

export default HomepagePricing;
